import { InterestSelector } from '../../components/inputs/interest-selector'
import { useState, type FormEvent } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { TextInput } from '../../components/inputs/textInput'
import { getInterestErrorMessage } from '../../redux-toolkits/interests/interests.helpers'
import {
  useCreateSpendingQuestionMutation,
  useUpdateSpendingQuestionMutation,
  type SpendingQuestion,
  type PricePreference,
  type SpendingQuestionBody,
} from '../../redux-toolkits/spending-questions/spending-question-slice'

const initialQuestion: SpendingQuestionBody = {
  title: 'How do you like to spend?',
  description:
    'No wrong answers. It helps us show prices that feel right to you.',
  isActive: false,
  displayOrder: 0,
  options: [
    { label: 'I look for affordable finds', pricePreference: 'affordable' },
    { label: 'I compare prices before deciding', pricePreference: 'balanced' },
    {
      label: 'I’m happy to spend more for quality',
      pricePreference: 'premium',
    },
    {
      label: 'I prioritise what I need, whatever the price',
      pricePreference: 'any',
    },
  ],
}

export function SpendingQuestionForm({
  question,
  onClose,
}: {
  question?: SpendingQuestion
  onClose: () => void
}) {
  const [form, setForm] = useState<SpendingQuestionBody>(
    question === undefined
      ? initialQuestion
      : { ...question, description: question.description ?? '' },
  )
  const [createQuestion, { isLoading: isCreating }] =
    useCreateSpendingQuestionMutation()
  const [updateQuestion, { isLoading: isUpdating }] =
    useUpdateSpendingQuestionMutation()
  const saving = isCreating || isUpdating

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (saving) return
    const options = form.options.map((option) => ({
      ...(option._id !== undefined ? { _id: option._id } : {}),
      label: option.label.trim(),
      icon: option.icon?.trim() ?? '',
      pricePreference: option.pricePreference ?? 'any',
      interestIds: option.interestIds ?? [],
    }))
    if (
      form.title.trim() === '' ||
      options.some((option) => option.label === '')
    ) {
      toast.error('Enter a question and a label for every option.')
      return
    }
    const labels = options.map((option) => option.label.toLowerCase())
    if (new Set(labels).size !== labels.length) {
      toast.error('Each option must have a different label.')
      return
    }
    const body: SpendingQuestionBody = {
      title: form.title.trim(),
      description: form.description.trim(),
      options,
      isActive: form.isActive,
      displayOrder: form.displayOrder,
    }
    try {
      if (question !== undefined)
        await updateQuestion({ id: question._id, body }).unwrap()
      else await createQuestion(body).unwrap()
      toast.success('Spending question saved')
      onClose()
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }

  return (
    <form
      onSubmit={(event) => {
        void save(event)
      }}
      className="space-y-5 rounded-2xl border border-divider-gray bg-white p-6"
    >
      <h2 className="text-xl font-semibold">
        {question === undefined ? 'Create question' : 'Edit question'}
      </h2>
      <fieldset disabled={saving} className="space-y-5">
        <TextInput
          title="Question"
          aria-label="Question"
          hasIcon={false}
          required
          maxLength={300}
          value={form.title}
          onChange={(event) => setForm({ ...form, title: event.target.value })}
        />
        <TextInput
          title="Description"
          aria-label="Description"
          hasIcon={false}
          maxLength={1000}
          value={form.description ?? ''}
          onChange={(event) =>
            setForm({ ...form, description: event.target.value })
          }
        />
        <div className="space-y-3">
          <h3 className="font-medium">Answer options</h3>
          <p className="text-sm text-pry-gray">
            Customers choose one option. Add between 2 and 20 options. Price
            preferences influence Discover ranking within comparable product
            categories; they do not hide other prices.
          </p>
          {form.options.map((option, index) => (
            <div
              key={option._id ?? index}
              className="flex flex-col gap-3 rounded-xl border border-divider-gray p-4"
            >
              <TextInput
                title={`Option ${index + 1}`}
                aria-label={`Option ${index + 1}`}
                hasIcon={false}
                required
                maxLength={300}
                value={option.label}
                onChange={(event) =>
                  setForm({
                    ...form,
                    options: form.options.map((item, position) =>
                      position === index
                        ? { ...item, label: event.target.value }
                        : item,
                    ),
                  })
                }
              />
              <TextInput
                title="Icon (optional)"
                aria-label={`Icon for option ${index + 1}`}
                hasIcon={false}
                maxLength={1000}
                value={option.icon ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    options: form.options.map((item, position) =>
                      position === index
                        ? { ...item, icon: event.target.value }
                        : item,
                    ),
                  })
                }
              />
              <label className="flex flex-col gap-2 text-sm">
                Price preference
                <select
                  className="rounded-lg border p-3"
                  value={option.pricePreference ?? 'any'}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      options: form.options.map((item, position) =>
                        position === index
                          ? {
                              ...item,
                              pricePreference: event.target
                                .value as PricePreference,
                            }
                          : item,
                      ),
                    })
                  }
                >
                  <option value="any">No preference</option>
                  <option value="affordable">Affordable</option>
                  <option value="balanced">Mid-range</option>
                  <option value="premium">Premium</option>
                </select>
              </label>
              <InterestSelector
                selectedIds={option.interestIds ?? []}
                description="Interests inferred when a customer selects this answer."
                onChange={(interestIds) =>
                  setForm({
                    ...form,
                    options: form.options.map((item, position) =>
                      position === index ? { ...item, interestIds } : item,
                    ),
                  })
                }
              />
              <button
                type="button"
                aria-label={`Remove option ${index + 1}`}
                disabled={form.options.length <= 2}
                className="p-3 text-danger disabled:opacity-40"
                onClick={() =>
                  setForm({
                    ...form,
                    options: form.options.filter(
                      (_, position) => position !== index,
                    ),
                  })
                }
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            disabled={form.options.length >= 20}
            className="font-medium text-primary disabled:opacity-40"
            onClick={() =>
              setForm({ ...form, options: [...form.options, { label: '' }] })
            }
          >
            Add option
          </button>
        </div>
        <TextInput
          title="Display order"
          aria-label="Display order"
          type="number"
          hasIcon={false}
          required
          min={0}
          step={1}
          value={String(form.displayOrder)}
          onChange={(event) =>
            setForm({ ...form, displayOrder: Number(event.target.value) })
          }
        />
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) =>
              setForm({ ...form, isActive: event.target.checked })
            }
          />
          Active — show this question to customers
        </label>
        <p className="text-sm text-pry-gray">
          Saved answers retain the wording customers originally selected.
        </p>
        <div className="flex gap-3">
          <Button
            label={saving ? 'Saving…' : 'Save question'}
            primary
            size="medium"
            type="submit"
          />
          <Button
            label="Cancel"
            size="medium"
            type="button"
            onClick={onClose}
          />
        </div>
      </fieldset>
    </form>
  )
}
